const MAX_SIZE_BYTES = 1024 * 1024 // 1 Mo
const TARGET_MIN_SIDE = 800 // 800 px pour le côté le plus court

/**
 * Redimensionne et compresse une image via Canvas si elle dépasse 1 Mo.
 * @param {File} file
 * @returns {Promise<File>} L'image compressée ou l'image originale si <= 1 Mo
 */
async function compressImageIfNeeded(file) {
  // 1. Si le fichier fait 1 Mo ou moins, ou n'est pas une image, on le renvoie tel quel
  if (!file || file.size <= MAX_SIZE_BYTES || !file.type.startsWith('image/')) {
    return file
  }

  return new Promise((resolve, reject) => {
    const img = new Image()
    const objectUrl = URL.createObjectURL(file)

    img.onload = () => {
      URL.revokeObjectURL(objectUrl) // Libère la mémoire

      let { width, height } = img

      // 2. Calcul des nouvelles dimensions (côté le plus court = 800px)
      if (width < height) {
        if (width > TARGET_MIN_SIDE) {
          height = Math.round((height * TARGET_MIN_SIDE) / width)
          width = TARGET_MIN_SIDE
        }
      } else {
        if (height > TARGET_MIN_SIDE) {
          width = Math.round((width * TARGET_MIN_SIDE) / height)
          height = TARGET_MIN_SIDE
        }
      }

      // 3. Dessin dans le canvas
      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height

      const ctx = canvas.getContext('2d')
      ctx.drawImage(img, 0, 0, width, height)

      // 4. Exportation en Blob avec compression JPEG (qualité 80%)
      canvas.toBlob(
        blob => {
          if (!blob) {
            resolve(file) // Fallback sur l'original si erreur d'exportation
            return
          }
          const compressedFile = new File([blob], file.name, {
            type: 'image/jpeg',
            lastModified: Date.now(),
          })
          resolve(compressedFile)
        },
        'image/jpeg',
        0.8,
      )
    }

    img.onerror = err => {
      URL.revokeObjectURL(objectUrl)
      reject(err)
    }

    img.src = objectUrl
  })
}

async function savePicture(picture) {
  // Compression à la volée avant l'appel d'upload
  const processedPicture = await compressImageIfNeeded(picture)
  const response = await uploadPicture(processedPicture)
  const parsedResponse = parseResponse(response)
  return parsedResponse
}

async function uploadPicture(picture) {
  let url = `https://api.monrapidoo.com/file-upload/file/upload`

  const requestData = new FormData()
  requestData.append('field', picture)

  const requestConfig = {
    method: 'post',
    headers: {},
    body: requestData,
  }
  let dataToReturn = {
    status: { code: 'not_connected' },
    response: null,
  }
  try {
    const response = await fetch(url, requestConfig)
    dataToReturn = {
      status: { code: response.status },
      response: await response.json(),
    }
  } catch (error) {
    console.warn(error)
  }

  return dataToReturn
}

function parseResponse(response) {
  if (
    response.status.code === 200 ||
    response.status.code === 201 ||
    response.status.code === 202
  ) {
    const data = {
      ...response.response,
      fileUrl:
        'https://api.monrapidoo.com/file-upload/file/download/' +
        response.response.id +
        response.response.ext,
    }
    return {
      success: true,
      message: '',
      data,
    }
  }
  if (response.status.code === 401) {
    return {
      success: false,
      unauthorized: true,
      message: '',
    }
  }
  if (response.status.code === 400) {
    const data = response.response?.data || {}
    return {
      success: false,
      unauthorized: true,
      badPictureError: !!data.badPicture,
      message: '',
    }
  }
  if (response.status.code === 403) {
    return {
      success: false,
      forbidden: true,
      message: 'Forbidden',
    }
  }
  if (response.status.code === 500) {
    return {
      success: false,
      message: 'Sorry, an error occured. Chec Please try again later',
    }
  }
  return {
    success: false,
    message: 'Sorry an error occured. Check your netword and retry',
  }
}

export { savePicture }
